import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const GamePlan = () => {
  const [gamePlans, setGamePlans] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [gameFilter, setGameFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 30;
  const [formData, setFormData] = useState({
    gameId: "",
    name: "",
    coin: "",
    receiverCoin: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchGamePlans();
    fetchGames();
  }, []);

  // Handle tab change
  const handleTabChange = (tabId, gameId = null) => {
    setActiveTab(tabId);
    setGameFilter(gameId);
    setCurrentPage(1);
    fetchGamePlans(gameId, 1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchGamePlans(gameFilter, newPage);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = startPage + maxPagesToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Fetch games for dropdown
  const fetchGames = async () => {
    try {
      const res = await fetch(`${baseUrl}/game/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status && result.data) {
        setGames(result.data);
      } else {
        console.error("Failed to fetch games:", result.message);
      }
    } catch (err) {
      console.error("Error fetching games:", err.message);
    }
  };

  // Fetch game plans
  const fetchGamePlans = async (gameId = null, page = 1) => {
    setLoading(true);
    try {
      let url = `${baseUrl}/game-plan/list?page=${page}&limit=${itemsPerPage}`;
      if (gameId) {
        url += `&gameId=${gameId}`;
      }
      console.log('Fetching game plans from:', url);
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to fetch game plans');
      }
      
      const result = await res.json();
      console.log('API Response:', result);
      
      if (result.status) {
        // Handle the API's pagination structure
        const pagination = result.pagination || {};
        const items = result.data || [];
        
        setGamePlans(items);
        setTotalPages(pagination.lastPage || 1);
        setTotalItems(pagination.total || items.length);
        setCurrentPage(pagination.currentPage || 1);
      } else {
        throw new Error(result.message || "Failed to fetch game plans");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setFormErrors({ ...formErrors, [name]: "" }); // clear error
  };

  // Validation
  const validateForm = () => {
    let errors = {};

    if (!formData.gameId) {
      errors.gameId = "Please select a game";
    }
    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = "Name must be at least 3 characters";
    }
    if (!formData.coin || isNaN(formData.coin) || Number(formData.coin) <= 0) {
      errors.coin = "Coin must be a positive number";
    }
    if (!formData.receiverCoin || isNaN(formData.receiverCoin) || Number(formData.receiverCoin) <= 0) {
      errors.receiverCoin = "Receiver Coin must be a positive number";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Normalize game plan response
  const normalizeGamePlan = (res) => {
    return res?.data?.gamePlan || res?.data || res?.gamePlan || res;
  };

  // Add new game plan
  const handleAddNew = async () => {
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${baseUrl}/game-plan/create`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Create failed");

      const newGamePlan = normalizeGamePlan(result);
      setGamePlans((prev) => [...prev, newGamePlan]);
      setFormData({ 
        gameId: "", 
        name: "", 
        coin: "", 
        receiverCoin: "" 
      });
      setShowModal(false);
      setEditingIndex(null);

      toast.success("Game plan created successfully!");
      await fetchGamePlans();
    } catch (error) {
      toast.error("Game plan creation failed!");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit click
  const handleEdit = (index) => {
    setEditingIndex(index);
    const plan = gamePlans[index];
    setFormData({
      gameId: plan.gameId || "",
      name: plan.name || "",
      coin: plan.coin || "",
      receiverCoin: plan.receiverCoin || "",
    });
    setShowModal(true);
  };

  // Save edited game plan
  const handleSave = async () => {
    if (!validateForm() || submitting) return;

    setSubmitting(true);
    const current = gamePlans[editingIndex];
    const planId = current._id || current.id;

    try {
      const res = await fetch(`${baseUrl}/game-plan/edit/${planId}`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      await fetchGamePlans();

      setFormData({ 
        gameId: "", 
        name: "", 
        coin: "", 
        receiverCoin: "" 
      });
      setShowModal(false);
      setEditingIndex(null);

      toast.success("Game plan updated successfully!");
    } catch (error) {
      toast.error("Game plan update failed!");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this game plan?")) return;
    try {
      const res = await fetch(`${baseUrl}/game-plan/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Delete failed");

      setGamePlans((prev) => prev.filter((g) => g.id !== id && g._id !== id));
      toast.success("Game plan deleted successfully!");
    } catch (error) {
      toast.error("Game plan deletion failed!");
    }
  };

  // Toggle active/inactive
  const toggleActive = async (id, isActive) => {
    try {
      const endpoint = isActive
        ? `${baseUrl}/game-plan/deactivate/${id}`
        : `${baseUrl}/game-plan/activate/${id}`;
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      setGamePlans((prev) =>
        prev.map((g) =>
          (g.id === id || g._id === id) ? { ...g, isActive: !isActive } : g
        )
      );
      toast.success(`Game plan ${isActive ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      toast.error("Game plan activation/deactivation failed!");
    }
  };

  return (
    <div className="flex bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Navbar />
        <div className="p-6">
          <h2 className="text-3xl font-bold mb-6 text-white">Game Plan Management</h2>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg">
              <button
                onClick={() => handleTabChange("all", null)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === "all"
                    ? "bg-yellow text-black"
                    : "text-gray-300 hover:text-white hover:bg-gray-700"
                }`}
              >
                All
              </button>
              {games.map((game) => (
                <button
                  key={game._id || game.id}
                  onClick={() => handleTabChange(game._id || game.id, game._id || game.id)}
                  className={`px-4 py-2 rounded-md font-medium transition-colors ${
                    activeTab === (game._id || game.id)
                      ? "bg-yellow text-black"
                      : "text-gray-300 hover:text-white hover:bg-gray-700"
                  }`}
                >
                  {game.name}
                </button>
              ))}
            </div>
          </div>

          {/* Add button */}
          <button
            onClick={() => {
              setEditingIndex(null);
              setFormData({ 
                gameId: "", 
                name: "", 
                coin: "", 
                receiverCoin: "" 
              });
              setShowModal(true);
            }}
            className="mb-6 bg-yellow text-black font-semibold px-4 py-2 rounded hover:bg-yellow-500"
          >
            Add Game Plan
          </button>

          {/* Modal */}
          {showModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-gray-800 p-6 rounded shadow-lg w-3/4 max-w-4xl max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-semibold mb-4 text-white">
                  {editingIndex !== null ? "Edit Game Plan" : "Add New Game Plan"}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <select
                      name="gameId"
                      value={formData.gameId}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    >
                      <option value="">Select a Game</option>
                      {games.map((game) => (
                        <option key={game._id || game.id} value={game._id || game.id}>
                          {game.name}
                        </option>
                      ))}
                    </select>
                    {formErrors.gameId && <p className="text-red-500 text-sm">{formErrors.gameId}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Game Plan Name"
                      value={formData.name}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.name && <p className="text-red-500 text-sm">{formErrors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="number"
                      name="coin"
                      placeholder="Coin Amount"
                      value={formData.coin}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.coin && <p className="text-red-500 text-sm">{formErrors.coin}</p>}
                  </div>
                  <div>
                    <input
                      type="number"
                      name="receiverCoin"
                      placeholder="Receiver Coin Amount"
                      value={formData.receiverCoin}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.receiverCoin && <p className="text-red-500 text-sm">{formErrors.receiverCoin}</p>}
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingIndex !== null ? handleSave : handleAddNew}
                    disabled={submitting}
                    className={`px-4 py-2 rounded flex items-center space-x-2 ${
                      submitting 
                        ? "bg-gray-400 text-gray-700 cursor-not-allowed" 
                        : "bg-yellow text-black hover:bg-yellow-500"
                    }`}
                  >
                    {submitting && (
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    <span>
                      {submitting 
                        ? (editingIndex !== null ? "Saving..." : "Creating...") 
                        : (editingIndex !== null ? "Save Changes" : "Add Game Plan")
                      }
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {loading && <p className="text-gray-400">Loading game plans...</p>}
          {error && <p className="text-red-500">{error}</p>}
          {!loading && gamePlans.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-gray-800 rounded shadow text-left">
                <thead className="bg-yellow">
                  <tr>
                    <th className="py-2 px-4">Game</th>
                    <th className="py-2 px-4">Name</th>
                    <th className="py-2 px-4">Coin</th>
                    <th className="py-2 px-4">Receiver Coin</th>
                    <th className="py-2 px-4">Status</th>
                    <th className="py-2 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gamePlans.map((item, index) => {
                    const selectedGame = games.find(game => (game._id || game.id) === item.gameId);
                    return (
                    <tr key={item.id || item._id} className="border-b border-gray-900 hover:bg-gray-700 text-gray-300">
                      <td className="py-2 px-4 font-semibold">{selectedGame ? selectedGame.name : item.gameId}</td>
                      <td className="py-2 px-4">{item.name}</td>
                      <td className="py-2 px-4">{item.coin}</td>
                      <td className="py-2 px-4">{item.receiverCoin}</td>
                      <td className="py-2 px-4">
                        <span
                          className={`px-2 py-1 rounded text-sm ${
                            item.isActive ? "bg-green-200 text-green-700" : "bg-red-200 text-red-700"
                          }`}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-2 px-4 space-x-2">
                        <button
                          className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                          onClick={() => handleEdit(index)}
                        >
                          Edit
                        </button>
                        <button
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                          onClick={() => handleDelete(item.id || item._id)}
                        >
                          Delete
                        </button>
                        <button
                          className={`px-3 py-1 rounded text-white ${
                            item.isActive ? "bg-gray-600 hover:bg-gray-700" : "bg-blue-600 hover:bg-blue-700"
                          }`}
                          onClick={() => toggleActive(item.id || item._id, item.isActive)}
                        >
                          {item.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && gamePlans.length === 0 && !error && (
            <div className="text-center py-8">
              <p className="text-gray-400 text-lg">No game plans found. Create your first game plan!</p>
            </div>
          )}

          {/* Pagination */}
          {!loading && gamePlans.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 px-4 py-3 bg-gray-800 rounded-lg space-y-2 sm:space-y-0">
              <div className="text-sm text-gray-400">
                Showing <span className="font-medium">
                  {totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                </span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, totalItems)}
                </span>{' '}
                of <span className="font-medium">{totalItems}</span> results
              </div>
              <div className="flex flex-wrap justify-center gap-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === 1 
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  aria-label="First page"
                >
                  &laquo;
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === 1 
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  aria-label="Previous page"
                >
                  &lsaquo;
                </button>
                
                {getPageNumbers().map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 rounded-md min-w-[32px] ${
                      currentPage === page
                        ? 'bg-yellow text-black font-medium'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    aria-current={currentPage === page ? 'page' : undefined}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className={`px-3 py-1 rounded-md ${
                    currentPage >= totalPages 
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  aria-label="Next page"
                >
                  &rsaquo;
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className={`px-3 py-1 rounded-md ${
                    currentPage >= totalPages 
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  aria-label="Last page"
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamePlan;